import {
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
  FormInput,
  Dialog,
  Button,
  Form,
} from '@ui/components';
import { PropsWithChildren, useCallback, useState, FC } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { z } from 'zod';

const formSchema = (t: TFunction, bannedList: string[]) =>
  z.object({
    email: z
      .string()
      .email({ message: t('FORMS.INVALID_EMAIL') })
      .min(1, { message: t('FORMS.REQUIRED') })
      .refine(
        (email) =>
          !bannedList.find((bannedEmail) => bannedEmail.toLowerCase() === email.toLowerCase()),
        { message: t('FORMS.EMAIL_ALREADY_EXISTS') },
      ),
  });

type EmailFormType = z.infer<ReturnType<typeof formSchema>>;

export const CostCenterCreateDocumentsReceiverDialog: FC<
  PropsWithChildren<{
    setReceiver: (receiver: EmailFormType) => void;
    recipients?: string[];
  }>
> = ({ setReceiver, recipients = [], children }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<EmailFormType>({
    resolver: zodResolver(formSchema(t, recipients)),
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
  });

  const onSubmit = useCallback(
    (data: EmailFormType) => {
      setReceiver(data);
      setIsOpen(false);
    },
    [setReceiver, setIsOpen],
  );

  return (
    <Dialog
      onOpenChange={(open) => {
        setIsOpen(open);
        form.reset();
      }}
      open={isOpen}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('COST_CENTERS.DOCUMENTS_RECEIVER_ADD_DIALOG_TITLE')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="p-4">
            <Form {...form}>
              <FormInput
                componentProps={{
                  hasShadow: true,
                }}
                placeholder={t('COST_CENTERS.DOCUMENTS_RECEIVER_EMAIL_PLACEHOLDER')}
                formControl={form.control}
                name="email"
                size="large"
                floating
                required
              />
            </Form>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="tertiary" type="button" wide>
                {t('COST_CENTERS.DOCUMENTS_RECEIVER_CANCEL')}
              </Button>
            </DialogClose>
            <Button variant="primary" type="submit" wide>
              {t('COST_CENTERS.DOCUMENTS_RECEIVER_SAVE')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
